/* Preserve compatible storefront filter parameters when navigating between ranges. */
(() => {
  if (document.documentElement.hasAttribute('data-collection-ranges-ready')) return;
  document.documentElement.setAttribute('data-collection-ranges-ready', '');

  /** @param {HTMLAnchorElement} link */
  function updateLink(link) {
    const navigation = /** @type {HTMLElement | null} */ (document.querySelector('[data-collection-ranges]'));
    if (!(navigation instanceof HTMLElement) || !link.dataset.rangeUrl) return;
    const excluded = (navigation.dataset.excludedFilters || '').split(',').map((name) => name.trim()).filter(Boolean);
    const destination = new URL(link.dataset.rangeUrl, window.location.origin);
    for (const [name, value] of new URLSearchParams(window.location.search)) {
      if ((name.startsWith('filter.') && !link.hasAttribute('data-range-clear-filters') && !excluded.includes(name)) || name === 'sort_by') {
        destination.searchParams.append(name, value);
      }
    }
    link.href = destination.href;
  }

  /** @type {AbortController | undefined} */
  let countController;

  /** @type {number | undefined} */
  let filtersDrawerScrollY;

  /** @param {HTMLElement} drawer */
  async function loadCounts(drawer) {
    countController?.abort();
    const controller = new AbortController();
    countController = controller;
    const list = drawer.querySelector('[data-range-rows]');
    const status = drawer.querySelector('[data-range-status]');
    if (!(list instanceof HTMLElement)) return;
    const rows = [...list.querySelectorAll('[data-range-row]')];
    if (status) status.textContent = '';
    for (const row of rows) {
      const reset = row.querySelector('[data-range-clear-filters]');
      if (reset instanceof HTMLElement) reset.hidden = true;
      const count = row.querySelector('[data-range-count]');
      if (count) count.textContent = list.dataset.loading || '';
    }
    let next = 0;
    /** @returns {Promise<void>} */
    async function worker() {
      while (next < rows.length && !controller.signal.aborted) {
        const row = rows[next++];
        const link = row?.querySelector('a[data-range-url]');
        const count = row?.querySelector('[data-range-count]');
        if (!(link instanceof HTMLAnchorElement) || !(count instanceof HTMLElement)) continue;
        updateLink(link);
        const url = new URL(link.href);
        const hasFilters = [...url.searchParams.keys()].some((key) => key.startsWith('filter.'));
        url.searchParams.set('section_id', 'custom_collection_range_count');
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok || new URL(response.url).pathname !== url.pathname) throw new Error('Invalid response');
          const html = await response.text();
          const result = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-range-count-result]');
          if (!(result instanceof HTMLElement) || !/^\d+$/.test(result.dataset.total || '') || !/^\d+$/.test(result.dataset.filtered || '')) throw new Error('Invalid count');
          if (controller.signal.aborted) return;
          const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined);
          const total = formatter.format(Number(result.dataset.total));
          const filtered = formatter.format(Number(result.dataset.filtered));
          const isEmpty = hasFilters && Number(result.dataset.filtered) === 0;
          const pattern = hasFilters
            ? (Number(result.dataset.filtered) === 1 ? list.dataset.resultOne : list.dataset.resultOther)
            : (Number(result.dataset.total) === 1 ? list.dataset.productOne : list.dataset.productOther);
          const value = hasFilters ? filtered : total;
          const parts = (pattern || '__count__').replace('__total__', total).split('__count__');
          count.textContent = parts[0] || '';
          const emphasis = document.createElement('strong');
          emphasis.textContent = value;
          count.append(emphasis, parts[1] || '');
          if (isEmpty) count.textContent = list.dataset.empty || '';
          const reset = row?.querySelector('[data-range-clear-filters]');
          if (reset instanceof HTMLElement) reset.hidden = !isEmpty;
        } catch (error) {
          if (controller.signal.aborted) return;
          count.textContent = list.dataset.unavailable || '';
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(3, rows.length) }, () => worker()));
    if (!controller.signal.aborted && status) status.textContent = list.dataset.complete || '';
  }

  /** @type {WeakMap<HTMLElement, Element>} */
  const revealedRanges = new WeakMap();

  /** Reveal the selected range without moving the page or other scroll containers.
   * @param {HTMLElement} list
   */
  function revealSelectedRange(list) {
    const active = list.querySelector('[aria-current="page"]');
    if (!(active instanceof HTMLElement) || list.clientWidth === 0) return;
    const viewport = list.getBoundingClientRect();
    const selected = active.getBoundingClientRect();
    if (selected.left < viewport.left || selected.right > viewport.right) {
      list.scrollBy({ left: selected.left + selected.width / 2 - viewport.left - viewport.width / 2, behavior: 'instant' });
    }
    revealedRanges.set(list, active);
  }

  const rangeResizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target instanceof HTMLElement) revealSelectedRange(entry.target);
    }
  });
  /** @type {HTMLElement | null} */
  let observedRangeList = null;

  /** Restore server-authored markup after standalone filter section responses. */
  function syncToolbar() {
    const navigation = /** @type {HTMLElement | null} */ (document.querySelector('[data-collection-ranges]'));
    const list = navigation?.querySelector('.twc-collection-ranges__list');
    const nextList = list instanceof HTMLElement ? list : null;
    if (observedRangeList !== nextList) {
      rangeResizeObserver.disconnect();
      observedRangeList = nextList;
      if (nextList) rangeResizeObserver.observe(nextList);
    }
    if (nextList && revealedRanges.get(nextList) !== nextList.querySelector('[aria-current="page"]')) {
      revealSelectedRange(nextList);
    }
    const toolbar = document.querySelector('.facets-toggle--drawer-trigger');
    if (!(toolbar instanceof HTMLElement)) return;
    const template = document.querySelector('[data-range-toolbar-template]');
    const hasRanges = navigation instanceof HTMLElement && template instanceof HTMLTemplateElement;
    const drawer = /** @type {(HTMLElement & {showDialog?: () => void}) | null} */ (document.querySelector('[data-range-drawer]'));
    toolbar.classList.toggle('twc-facets-with-ranges', hasRanges);
    toolbar.classList.remove('custom-range-toolbar-class');
    if (hasRanges && !toolbar.querySelector('[data-range-control]')) toolbar.append(template.content.cloneNode(true));
    if (!hasRanges) toolbar.querySelector('[data-range-control]')?.remove();
    const suffix = toolbar.querySelector('[data-range-count-suffix]');
    const context = hasRanges ? navigation.dataset.countSuffix || '' : '';
    if (suffix && suffix.textContent !== context) suffix.textContent = context;
    syncFilterTriggers(navigation, drawer, hasRanges);
  }

  /** Add configured macro-range controls below their matching Shopify filter panels.
   * @param {HTMLElement | null} navigation
   * @param {HTMLElement | null} drawer
   * @param {boolean} hasRanges
   */
  function syncFilterTriggers(navigation, drawer, hasRanges) {
    document.querySelectorAll('[data-range-filter-trigger]').forEach((trigger) => trigger.remove());
    if (!(navigation instanceof HTMLElement) || !(drawer instanceof HTMLElement) || !hasRanges) return;
    const params = (navigation.dataset.filterTriggerParams || '').split(',').map((value) => value.trim()).filter(Boolean);
    const label = navigation.dataset.filterTriggerLabel || '';
    if (!params.length || !label) return;
    document.querySelectorAll('accordion-custom[data-custom-filter-param]').forEach((accordion) => {
      if (!(accordion instanceof HTMLElement) || !params.includes(accordion.dataset.customFilterParam || '')) return;
      const panel = accordion.querySelector('floating-panel-component');
      if (!(panel instanceof HTMLElement)) return;
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'twc-range-filter-trigger';
      trigger.dataset.rangeFilterTrigger = '';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', drawer.id);
      trigger.textContent = label;
      let openingRequested = false;
      /** @param {Event} event */
      const requestDrawerOpen = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (openingRequested) return;
        openingRequested = true;
        // Horizontal facet panels can disappear at pointerdown before their click event fires.
        window.setTimeout(() => openRangeDrawer(drawer));
      };
      trigger.addEventListener('pointerdown', requestDrawerOpen);
      trigger.addEventListener('click', requestDrawerOpen);
      panel.append(trigger);
    });
  }

  /** @param {boolean} hasActiveFilters */
  function syncRangeFilterContext(hasActiveFilters) {
    document.querySelectorAll('[data-range-filter-context]').forEach((context) => {
      if (context instanceof HTMLElement) context.hidden = !hasActiveFilters;
    });
  }

  /** Returns the collection page to its beginning without releasing an open filter drawer. */
  function scrollPageToTop() {
    const filtersDrawer = document.querySelector('#filters-drawer');
    const filtersDialog = filtersDrawer?.querySelector('dialog[open]');
    if (filtersDrawer instanceof HTMLElement && filtersDialog instanceof HTMLDialogElement) {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = '0px';
      window.scrollTo({ top: 0, behavior: 'instant' });
      filtersDrawer.addEventListener('dialog:close', () => {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }, { once: true });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {Event} event */
  function handleFacetFilterUpdate(event) {
    const detail = /** @type {{ queryParams?: URLSearchParams }} */ (/** @type {CustomEvent} */ (event).detail);
    const queryParams = detail?.queryParams;
    if (!(queryParams instanceof URLSearchParams)) return;
    syncRangeFilterContext([...queryParams.keys()].some((name) => name.startsWith('filter.')));
    scrollPageToTop();
  }

  /** @param {Event} event */
  function handleTagFilterUpdate(event) {
    const detail = /** @type {{ hasActiveFilters?: boolean }} */ (/** @type {CustomEvent} */ (event).detail);
    if (typeof detail?.hasActiveFilters !== 'boolean') return;
    syncRangeFilterContext(detail.hasActiveFilters);
    scrollPageToTop();
  }

  /** Restores the page lock when the parent filter drawer remains open. */
  function restoreFiltersDrawerScrollLock() {
    if (!document.querySelector('#filters-drawer dialog[open]')) return;
    const scrollY = filtersDrawerScrollY || 0;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {HTMLElement & {showDialog?: () => void}} drawer */
  function openRangeDrawer(drawer) {
    if (!drawer.showDialog) return;
    const filtersDrawer = document.querySelector('#filters-drawer dialog[open]');
    if (filtersDrawer instanceof HTMLDialogElement) {
      const top = Number.parseFloat(document.body.style.top);
      filtersDrawerScrollY = Number.isFinite(top) ? Math.abs(top) : window.scrollY;
    }
    drawer.addEventListener('dialog:close', () => {
      countController?.abort();
      restoreFiltersDrawerScrollLock();
    }, { once: true });
    drawer.showDialog();
    void loadCounts(drawer);
  }

  // Capture the toolbar click before horizontal facet popovers close or stop propagation on desktop.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-range-control]');
    if (!control) return;
    const drawer = /** @type {(HTMLElement & {showDialog?: () => void}) | null} */ (document.querySelector('[data-range-drawer]'));
    if (!drawer) return;
    event.preventDefault();
    openRangeDrawer(drawer);
  }, true);

  /** @param {Event} event */
  function refreshLink(event) {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-collection-ranges] a[data-range-url], [data-range-drawer] a[data-range-url]');
    if (link instanceof HTMLAnchorElement) updateLink(link);
  }

  document.addEventListener('click', refreshLink, true);
  document.addEventListener('contextmenu', refreshLink, true);
  document.addEventListener('focusin', refreshLink, true);
  document.addEventListener('filter:update', handleFacetFilterUpdate);
  document.addEventListener('custom:tag-filter-update', handleTagFilterUpdate);
  /** @type {AbortController | undefined} */
  let navigationController;
  const announcement = document.createElement('div');
  announcement.className = 'twc-range-navigation-status';
  announcement.setAttribute('role', 'status');
  announcement.hidden = true;
  document.body.append(announcement);
  const loadingOverlay = document.createElement('dialog');
  loadingOverlay.className = 'twc-range-loading-overlay';
  loadingOverlay.setAttribute('aria-hidden', 'true');
  loadingOverlay.hidden = true;
  loadingOverlay.addEventListener('cancel', (event) => event.preventDefault());
  const spinner = document.createElement('span');
  spinner.className = 'twc-range-loading-overlay__spinner';
  loadingOverlay.append(spinner);
  document.body.append(loadingOverlay);

  /** @param {boolean} visible */
  function setLoadingOverlay(visible) {
    if (visible) {
      loadingOverlay.hidden = false;
      if (!loadingOverlay.open) loadingOverlay.showModal();
      return;
    }
    if (loadingOverlay.open) loadingOverlay.close();
    loadingOverlay.hidden = true;
  }

  /** @returns {Promise<void>} */
  function nextAnimationFrame() {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  /** @param {URL} destination @param {boolean} pushHistory */
  async function navigateRange(destination, pushHistory) {
    navigationController?.abort();
    const controller = new AbortController();
    navigationController = controller;
    countController?.abort();
    const filtersDrawer = /** @type {(HTMLElement & {closeDialog?: () => Promise<void>}) | null} */ (document.querySelector('#filters-drawer'));
    const filtersDrawerWasOpen = filtersDrawer?.querySelector('dialog[open]') instanceof HTMLDialogElement;
    const main = document.querySelector('#MainContent');
    const nav = document.querySelector('[data-collection-ranges]');
    if (!(main instanceof HTMLElement) || !(nav instanceof HTMLElement)) {
      window.location.assign(destination.href);
      return;
    }
    const loading = nav.dataset.navigationLoading || '';
    const completed = nav.dataset.navigationComplete || '';
    announcement.textContent = loading;
    announcement.hidden = false;
    setLoadingOverlay(true);
    const drawer = /** @type {(HTMLElement & {closeDialog?: () => Promise<void>}) | null} */ (document.querySelector('[data-range-drawer]'));
    try {
      if (drawer?.querySelector('dialog[open]') && drawer.closeDialog) await drawer.closeDialog();
      if (filtersDrawerWasOpen && filtersDrawer?.closeDialog) await filtersDrawer.closeDialog();
      if (controller.signal.aborted) return;
      main.inert = true;
      main.setAttribute('aria-busy', 'true');
      const [{ morphSection, sectionRenderer }, response] = await Promise.all([
        import('@theme/section-renderer'),
        fetch(destination, { signal: controller.signal, headers: { 'Accept': 'text/html' } }),
      ]);
      if (!response.ok || new URL(response.url).pathname !== destination.pathname) throw new Error('Unexpected collection response');
      const html = await response.text();
      if (controller.signal.aborted) return;
      const page = new DOMParser().parseFromString(html, 'text/html');
      const nextMain = page.querySelector('#MainContent');
      if (!(nextMain instanceof HTMLElement) || main.dataset.template !== nextMain.dataset.template) throw new Error('Different template');
      const sections = [...main.children];
      const nextSections = [...nextMain.children];
      if (!sections.length || sections.length !== nextSections.length || sections.some((section, index) => !section.id.startsWith('shopify-section-') || section.id !== nextSections[index]?.id)) throw new Error('Different sections');
      if (!nextMain.querySelector('[data-collection-ranges]') || !nextMain.querySelector('results-list')) throw new Error('Missing collection content');
      const breadcrumb = document.querySelector('#header-group .twc-breadcrumbs')?.closest('.shopify-section');
      const nextBreadcrumb = page.querySelector('#header-group .twc-breadcrumbs')?.closest('.shopify-section');
      if (breadcrumb || nextBreadcrumb) {
        if (!breadcrumb?.id || breadcrumb.id !== nextBreadcrumb?.id) throw new Error('Different breadcrumbs');
        sections.unshift(breadcrumb);
        nextSections.unshift(nextBreadcrumb);
      }
      // Inline executable scripts can contain collection-specific state that morphing cannot re-run.
      for (let i = 0; i < sections.length; i++) {
        const selector = 'script:not([type="application/json"]):not([type="application/ld+json"]):not([data-range-navigation-inert])';
        const scripts = (/** @type {Element} */ (sections[i])).querySelectorAll(selector);
        const nextScripts = nextSections[i]?.querySelectorAll(selector);
        if (!nextScripts || scripts.length !== nextScripts.length || [...scripts].some((script, index) => script.getAttribute('src') !== nextScripts[index]?.getAttribute('src') || script.textContent !== nextScripts[index]?.textContent)) throw new Error('Collection scripts differ');
      }
      for (const section of sections) sectionRenderer.cancelPending(section.id);
      if (pushHistory) history.pushState({ customCollectionRange: true }, '', destination.href);
      for (const section of sections) {
        if (controller.signal.aborted) throw new Error('Navigation superseded');
        await morphSection(section.id.replace(/^shopify-section-/, ''), html, { mode: 'full' });
      }
      document.title = page.title;
      for (const selector of ['link[rel="canonical"]', 'meta[name="description"]', 'meta[name="robots"]', 'meta[property^="og:"]', 'meta[name^="twitter:"]']) {
        document.head.querySelectorAll(selector).forEach((node) => node.remove());
        page.head.querySelectorAll(selector).forEach((node) => document.head.append(node.cloneNode(true)));
      }
      main.inert = false;
      main.removeAttribute('aria-busy');
      syncToolbar();
      if (filtersDrawerWasOpen) {
        const nextFiltersDrawer = /** @type {(HTMLElement & {showDialog?: () => void}) | null} */ (document.querySelector('#filters-drawer'));
        if (nextFiltersDrawer?.showDialog) {
          nextFiltersDrawer.showDialog();
          await nextAnimationFrame();
        }
      }
      if (pushHistory) scrollPageToTop();
      setLoadingOverlay(false);
      const control = main.querySelector('[data-range-control]');
      if (control instanceof HTMLElement) control.focus({ preventScroll: true });
      announcement.textContent = completed;
      setTimeout(() => { if (navigationController === controller) announcement.hidden = true; }, 2000);
    } catch (error) {
      if (controller.signal.aborted) return;
      window.location.assign(destination.href);
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event instanceof MouseEvent) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
    const link = event.target.closest('[data-collection-ranges] a[data-range-url], [data-range-drawer] a[data-range-url]');
    if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download') || (link.target && link.target !== '_self') || window.Shopify?.designMode) return;
    updateLink(link);
    event.preventDefault();
    void navigateRange(new URL(link.href), true);
  });

  window.addEventListener('popstate', () => {
    const links = document.querySelectorAll('[data-collection-ranges] a[data-range-url]');
    const current = new URL(window.location.href);
    const isRange = [...links].some((link) => link instanceof HTMLAnchorElement && link.dataset.rangeUrl && new URL(link.dataset.rangeUrl, location.origin).pathname === current.pathname);
    if (!isRange) { window.location.reload(); return; }
    void navigateRange(current, false);
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    setLoadingOverlay(false);
    announcement.hidden = true;
    const main = document.querySelector('#MainContent');
    if (main instanceof HTMLElement) {
      main.inert = false;
      main.removeAttribute('aria-busy');
    }
  });
  syncToolbar();
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      syncToolbar();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
